import { ChangeEvent, FC, useState } from "react";
import { Button, Checkbox, Modal, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import { StyledCheckboxContainer, StyledModalActions } from "./StyledComponents";
import { FormLabel } from "@kaltura/mediaspace-shared-ui/form";

export interface WelcomeProps {
    /**
     * Whether the modal is open
     */
    open: boolean;

    /**
     * Callback when the modal is closed
     */
    onClose: () => void;

    /**
     * Callback when the "Don't show again" checkbox is changed
     */
    onDontShowAgainChange?: (dontShowAgain: boolean) => void;

    /**
     * Title of the modal
     */
    title: string;

    /**
     * Content text of the modal
     */
    contentText: string;
}

/**
 * Welcome modal for the Live Studio
 */
export const Welcome: FC<WelcomeProps> = ({ open, onClose, onDontShowAgainChange, title, contentText }) => {
    const [dontShowAgain, setDontShowAgain] = useState(false);

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setDontShowAgain(checked);
        if (onDontShowAgainChange) {
            onDontShowAgainChange(checked);
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="welcome-modal-title"
            className="kms-ds-live-studio-welcome-modal"
        >
            <ModalTitle id="welcome-modal-title">{title}</ModalTitle>
            <ModalContent>
                <Typography variant="body1" align="center" className="kms-ds-welcome-modal-description">
                    {HtmlReactParser(contentText)}
                </Typography>
            </ModalContent>

            <StyledModalActions className="kms-ds-welcome-modal-actions">
                <StyledCheckboxContainer>
                    <Checkbox
                        id="dont-show-again"
                        checked={dontShowAgain}
                        onChange={handleCheckboxChange}
                        className="kms-ds-welcome-modal-checkbox"
                    />
                    <FormLabel htmlFor="dont-show-again" text={translate("Don't show again")} />
                </StyledCheckboxContainer>

                <Button onClick={handleClose} className="kms-ds-welcome-modal-close-button">
                    {translate("Close")}
                </Button>
            </StyledModalActions>
        </Modal>
    );
};

export default Welcome;
